/**
 * Creators who asked to promote this brand — and the brand's decision.
 *
 * WHY THIS PANEL EXISTS. A brand could already switch its catalog to "approved
 * creators only" from the tab next door, and then had no way to see a single
 * request: they landed in GNCreators' Django admin, actionable only by GN ops.
 * A brand managing a gate it cannot see is not a feature, it is a support
 * queue. This is the missing half.
 *
 * The creator data is proxied from GNCreators, which is where creators exist —
 * this app has no creator records. Two consequences shape the UI:
 *
 *  - **A failure is not an empty inbox.** If GNCreators is unreachable the API
 *    answers 503, and this renders an error rather than "no requests", which
 *    would read as "nobody asked" and is the wrong thing to tell a brand that
 *    is waiting on people.
 *  - **Only what a brand needs to decide is shown** — name, handle, followers,
 *    niches. No email, no phone. Recognising a creator is the job; contacting
 *    them outside the platform is not.
 *
 * Approve is one click. Reject and revoke ask for confirmation and offer an
 * optional reason, because both take something away and the creator sees the
 * reason verbatim.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  palette,
  radius,
  shadow,
  motion,
} from '../../_helpers/reusableComponents/design/designTokens';
import CommerceService, { readApiError } from '../../_services/commerce/CommerceService';

/** 12345 -> "12.3K". A brand scans this column; exact digits help nobody. */
const compactCount = (n) => {
  const value = Number(n) || 0;
  if (value < 1000) return String(value);
  if (value < 100000) return `${(value / 1000).toFixed(value < 10000 ? 1 : 0)}K`;
  return `${(value / 100000).toFixed(1)}L`;   // lakh — the unit this audience reads
};

const STATUS_CHIP = {
  REQUESTED: { label: 'Awaiting you', fg: '#92400E', bg: '#FEF3C7' },
  APPROVED: { label: 'Approved', fg: '#065F46', bg: '#DCFCE7' },
  REJECTED: { label: 'Not approved', fg: '#9F1239', bg: '#FEE2E2' },
  REVOKED: { label: 'Access withdrawn', fg: '#9F1239', bg: '#FEE2E2' },
};

const RequestRow = ({ row, busy, onApprove, onRefuse }) => {
  const chip = STATUS_CHIP[row.status] || STATUS_CHIP.REQUESTED;
  const pending = row.status === 'REQUESTED';
  const approved = row.status === 'APPROVED';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        border: `1px solid ${palette.line}`,
        borderRadius: `${radius.md}px`,
        bgcolor: pending ? palette.surfaceAlt : palette.surface,
        transition: `box-shadow ${motion.base}`,
        '&:hover': { boxShadow: shadow.card },
      }}
    >
      <Avatar
        src={row.avatar_url || undefined}
        alt=""
        sx={{ width: 44, height: 44, bgcolor: palette.brandSoft, color: palette.brand }}
      >
        {(row.display_name || '?').charAt(0).toUpperCase()}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            sx={{
              fontWeight: 700,
              color: palette.ink,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.display_name}
          </Typography>
          <Chip
            size="small"
            label={chip.label}
            sx={{ height: 20, fontSize: 11, fontWeight: 700, color: chip.fg, bgcolor: chip.bg }}
          />
        </Stack>

        <Typography sx={{ fontSize: 13, color: palette.muted }}>
          {row.username ? `@${row.username} · ` : ''}
          {compactCount(row.followers_count)} followers
          {row.niches?.length ? ` · ${row.niches.join(', ')}` : ''}
        </Typography>

        {row.decision_note && (
          <Tooltip title={row.decision_note} arrow>
            <Typography
              sx={{
                fontSize: 12,
                fontStyle: 'italic',
                color: palette.inkSoft,
                mt: 0.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              “{row.decision_note}”
            </Typography>
          </Tooltip>
        )}
      </Box>

      <Stack direction="row" spacing={1}>
        {pending && (
          <>
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={() => onApprove(row)}
              startIcon={<CheckCircleOutlineIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: palette.success,
                '&:hover': { bgcolor: palette.success },
              }}
            >
              Approve
            </Button>
            <Button
              size="small"
              disabled={busy}
              onClick={() => onRefuse(row, 'REJECTED')}
              sx={{ textTransform: 'none', color: palette.muted }}
            >
              Reject
            </Button>
          </>
        )}

        {approved && (
          <Button
            size="small"
            disabled={busy}
            onClick={() => onRefuse(row, 'REVOKED')}
            startIcon={<BlockOutlinedIcon />}
            sx={{ textTransform: 'none', color: palette.danger }}
          >
            Withdraw
          </Button>
        )}
      </Stack>
    </Box>
  );
};

const CreatorAccessRequests = ({ onNotify }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [refusing, setRefusing] = useState(null);   // {row, status}
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await CommerceService.listAccessRequests();
      setData(resp.data);
      setError('');
    } catch (err) {
      // 503 means GNCreators is unreachable. Saying "no requests" here would
      // tell a brand nobody asked, which may be the opposite of the truth.
      setError(readApiError(err, 'Could not load creator requests.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = useCallback(
    async (row, status, reason = '') => {
      setBusyId(row.handler_id);
      try {
        const resp = await CommerceService.decideAccessRequest(
          row.handler_id, status, reason,
        );
        // The API returns the refreshed list, so the panel can never render a
        // stale inbox beside a decision just made.
        setData(resp.data);
        onNotify?.(
          status === 'APPROVED'
            ? `${row.display_name} can now promote your products.`
            : `${row.display_name} has been ${status === 'REVOKED' ? 'withdrawn' : 'declined'}.`,
          'success',
        );
      } catch (err) {
        onNotify?.(readApiError(err, 'Could not save that decision.'), 'error');
      } finally {
        setBusyId(null);
        setRefusing(null);
        setNote('');
      }
    },
    [onNotify],
  );

  const rows = data?.results || [];
  const pending = useMemo(
    () => rows.filter((r) => r.status === 'REQUESTED'),
    [rows],
  );
  const settled = useMemo(
    () => rows.filter((r) => r.status !== 'REQUESTED'),
    [rows],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} sx={{ color: palette.brand }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="warning"
        sx={{ borderRadius: `${radius.sm}px` }}
        action={
          <Button size="small" onClick={load} startIcon={<RefreshOutlinedIcon />}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  const gated = data?.brand?.creator_access === 'APPROVAL';

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <GroupsOutlinedIcon sx={{ color: palette.brand }} />
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: palette.ink, flex: 1 }}>
          Creator requests
          {pending.length > 0 && (
            <Chip
              size="small"
              label={pending.length}
              sx={{
                ml: 1, height: 20, fontSize: 11, fontWeight: 700,
                color: palette.warning, bgcolor: palette.warningSoft,
              }}
            />
          )}
        </Typography>
        <Button size="small" onClick={load} startIcon={<RefreshOutlinedIcon />}
                sx={{ textTransform: 'none', color: palette.muted }}>
          Refresh
        </Button>
      </Stack>

      {/* Said plainly: with an open catalog nobody needs to ask, so an empty
          list here is expected rather than a sign something is broken. */}
      {!gated && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: `${radius.sm}px` }}>
          Your catalog is open to every enrolled creator, so nobody needs to
          request access. Requests appear here only while “Approved creators
          only” is selected above.
        </Alert>
      )}

      {rows.length === 0 ? (
        <Box
          sx={{
            py: 5, textAlign: 'center',
            border: `2px dashed ${palette.line}`,
            borderRadius: `${radius.md}px`,
            bgcolor: palette.surfaceAlt,
          }}
        >
          <Typography sx={{ color: palette.muted, fontSize: 14 }}>
            No creator requests yet.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {/* Pending first: an operator opens this to act, not to browse. */}
          {[...pending, ...settled].map((row) => (
            <RequestRow
              key={row.handler_id}
              row={row}
              busy={busyId === row.handler_id}
              onApprove={(r) => submit(r, 'APPROVED')}
              onRefuse={(r, status) => setRefusing({ row: r, status })}
            />
          ))}
        </Stack>
      )}

      <Dialog open={Boolean(refusing)} onClose={() => setRefusing(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {refusing?.status === 'REVOKED' ? 'Withdraw access?' : 'Decline this creator?'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: palette.inkSoft, mb: 2 }}>
            {refusing?.status === 'REVOKED'
              ? `${refusing?.row?.display_name} will not be able to add new products. Anything already on their storefront keeps working, and commission they have already earned is unaffected.`
              : `${refusing?.row?.display_name} will not be able to promote your products. They can ask again later.`}
          </Typography>
          <TextField
            label="Reason (optional)"
            helperText="The creator sees this, so keep it something you would say to them."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRefusing(null)} sx={{ textTransform: 'none', color: palette.muted }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => submit(refusing.row, refusing.status, note)}
            sx={{
              textTransform: 'none', fontWeight: 700,
              bgcolor: palette.danger, '&:hover': { bgcolor: palette.danger },
            }}
          >
            {refusing?.status === 'REVOKED' ? 'Withdraw' : 'Decline'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreatorAccessRequests;
